import { rem } from 'polished'
import type { CSSProperties } from 'react'
import { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import useHasMounted from 'hooks/useHasMounted'
import BodyText from 'LuxKit/Typography/BodyText'
import zIndex from 'styles/tools/z-index'

/**
 * Hover bubble for calendar event copy.
 *
 * Hand-positioned rather than using `LuxKit/Tooltip`, whose sheet is pinned to
 * `zIndex.tooltip` (10) and so renders behind the date-picker sheet. Raising that
 * token is a design-system change affecting every tooltip in the app.
 */
const Bubble = styled.div`
  position: fixed;
  z-index: ${zIndex.draggingItem};
  pointer-events: none;
  max-width: min(${rem(280)}, calc(100vw - ${rem(32)}));
  padding: ${rem(8)} ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
`

function getBubbleStyle(target: HTMLElement): CSSProperties {
  const rect = target.getBoundingClientRect()
  const centre = rect.left + rect.width / 2

  return {
    top: rect.top - 8,
    left: Math.min(Math.max(centre, 16), window.innerWidth - 16),
    transform: 'translate(-50%, -100%)',
  }
}

/**
 * Positions a bubble above whichever element triggered it.
 *
 * Returns show/hide callbacks plus the node to render. The caller owns the
 * accessible description — the bubble itself is decorative.
 */
export function useCalendarEventTooltip(text: string, testId?: string) {
  const hasMounted = useHasMounted()
  const [style, setStyle] = useState<CSSProperties | undefined>()

  const show = useCallback((target: HTMLElement) => {
    setStyle(getBubbleStyle(target))
  }, [])

  const hide = useCallback(() => {
    setStyle(undefined)
  }, [])

  let bubble
  if (hasMounted && style) {
    bubble = createPortal(
      <Bubble aria-hidden data-testid={testId} style={style}>
        <BodyText variant="small" colour="dark-contrast" align="start">
          {text}
        </BodyText>
      </Bubble>,
      document.body,
    )
  }

  return { bubble, hide, show }
}
